import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  TablePagination, Collapse, Box, Typography, Stack, Link, Tooltip, TextField
} from '@mui/material';
import { 
  KeyboardArrowDown, KeyboardArrowUp, SignalCellular4Bar, SignalCellular3Bar, 
  SignalCellular2Bar, SignalCellular1Bar, SignalCellular0Bar, Battery90, Battery60, 
  Battery30, BatteryAlert, CheckCircle, Cancel 
} from '@mui/icons-material';
import API_BASE_URL from '../config';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import CircularProgress from '@mui/material/CircularProgress';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';

import rgbImage from '../assets/rgb.png';
import rgImage from '../assets/rg.png';

import './Overview.css';

const Overview = () => {
  const [devices, setDevices] = useState([]);
  const [cabinTemps, setCabinTemps] = useState({});
  const [devicePlaying, setDevicePlaying] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  const [mediaImages, setMediaImages] = useState({});
  const mediaImagesRef = useRef({});

  const [isFlashOn, setIsFlashOn] = useState(true);
  const [isSwitchOn, setIsSwitchOn] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState({
    group: '',
    label: '',
    model: '',
    location: '',
    template: ''
  });
  
  const handleSearchChange = (e, field) => {
    setSearchCriteria({
      ...searchCriteria,
      [field]: e.target.value
    });
  };

  const filteredDevices = devices.filter(device => {
    const groupMatch = device?.groups?.length > 0 ? device.groups.some(group => group.group_name.toLowerCase().includes(searchCriteria.group.toLowerCase())) : searchCriteria.group === '';
    const labelMatch = (device.device_name || 'Unknown').toLowerCase().includes(searchCriteria.label.toLowerCase());
    const modelMatch = (device.device_type || 'Unknown').toLowerCase().includes(searchCriteria.model.toLowerCase());
    const locationMatch = device.street_address.toLowerCase().includes(searchCriteria.location.toLowerCase());
  
    return groupMatch && labelMatch && modelMatch && locationMatch;
  });
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlashOn(prev => !prev);
    }, 500); // 0.5 seconds
  
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsSwitchOn(prev => !prev);
    }, 1000); // 1.0 seconds
  
    return () => clearInterval(interval);
  }, []);


  const navigate = useNavigate();

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    if (devices.length > 0) {
      // fetchAllCabinTemps();
      fetchAllPlaying();
    }
  }, [devices]);

  useEffect(() => {
    const checkLocalStorage = () => {
      const storedPrimaryColor = localStorage.getItem('primaryColor');
      const storedSecondaryColor = localStorage.getItem('secondaryColor');
      const storedDarkMode = localStorage.getItem('darkMode') === 'true';

      if (storedPrimaryColor !== primaryColor) {
        setPrimaryColor(storedPrimaryColor);
      }
      if (storedSecondaryColor !== secondaryColor) {
        setSecondaryColor(storedSecondaryColor);
      }
      if (storedDarkMode !== darkMode) {
        setDarkMode(storedDarkMode);
      }
    };

    checkLocalStorage();
    const intervalId = setInterval(checkLocalStorage, 200);

    return () => clearInterval(intervalId);
  }, [primaryColor, secondaryColor, darkMode]);

  useEffect(() => {
    updateColors(darkMode);
  }, [darkMode, textColor]);


  const updateColors = (isDarkMode) => {
    if (isDarkMode) {
      setPrimaryColor('#0f0f0f');
      setSecondaryColor('#1a1a1a');
      setTextColor('#ffffff');
    } else {
      setPrimaryColor('#f5f5f5');
      setSecondaryColor('#e0e0e0');
      setTextColor('#000000')
    }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const deviceData = await response.json();
      setDevices(deviceData);
      setError(null);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  /*
  const fetchAllCabinTemps = async () => {
    try {
      const promises = devices.map(device => fetchCabinTemp(device.id));
      const results = await Promise.all(promises);
      const newCabinTemps = {};
      devices.forEach((device, index) => {
        newCabinTemps[device.device_name] = results[index];
      });
      setCabinTemps(newCabinTemps);
    } catch (error) {
      console.error('Error fetching cabin temperatures:', error);
    }
  };

  const fetchCabinTemp = async (deviceName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-cabin-temp/${deviceName}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.cabinTemperature;
    } catch (error) {
      if (error.message.includes('401')) {
        navigate('/unauthorized');
      } else {
        console.error('Error fetching cabin temperature:', error);
      }
      return 'N/A';
    }
  };
  */

  const fetchPlaying = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/overview-playing/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      return data;
    } catch (error) {
      if (error.message.includes('401')) {
        navigate('/unauthorized');
      } else {
        console.error(`Error fetching playing status for device ID ${deviceId}:`, error);
      }
      return null;
    }
  };
  
  const fetchAllPlaying = async () => {
    try {
      const promises = devices.map(device => fetchPlaying(device.id));
      const results = await Promise.all(promises);

      const newDevicePlaying = {};
      devices.forEach((device, index) => {
        newDevicePlaying[device.device_name] = results[index];
      });
      setDevicePlaying(newDevicePlaying);
    } catch (error) {
      console.error('Error fetching playing status:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeviceClick = (deviceId) => {
    navigate(`/device/${deviceId}`);
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  const handleAddrClick = (deviceId) => {
    navigate(`/maps`);
  }

  const handleTemplateClick = (templateId) => {
    navigate(`/presets/${templateId}`);
  };

  const getSignalIcon = (value) => {
    const bars = Math.max(0, Math.min(5, Math.floor((value + 109) / 12) + 1));

    if (value === null || value === undefined || value === 0) {
      return <SignalCellular1BarIcon color="warning" />;
    }

    // Clamp the dBm value between -113 and -51
    const clampedDBm = Math.max(-113, Math.min(value, -51));


    // Calculate the percentage of signal strength
    const signalPercentage = (clampedDBm + 113) / (62) * 100;

    if (signalPercentage >= 80) return <SignalCellular4BarIcon color="success" />;
    if (signalPercentage >= 60) return <SignalCellular3BarIcon color="success" />;
    if (signalPercentage >= 40) return <SignalCellular2BarIcon color="warning" />;
    if (signalPercentage >= 20) return <SignalCellular1BarIcon color="warning" />;
    return <SignalCellular1BarIcon color="warning" />
  };

  const getBatteryIcon = (percentage) => {
    if (percentage === -1) return <BatteryAlertIcon color="error" />;
    if (percentage >= 80) return <BatteryFullIcon color="success" />;
    if (percentage >= 50) return <Battery80Icon color="success" />;
    if (percentage >= 20) return <Battery50Icon color="warning" />;
    if (percentage >= 0 && percentage < 20) return <Battery20Icon color="error" />;
    return <BatteryAlertIcon color="error" />;
  };

  const fetchMediaImage = useCallback(async (mediaName) => {
    if (mediaName === null || mediaName === undefined) return null;
    try {
        const response = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(mediaName)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error(`Error fetching media image for ${mediaName}:`, error);
        return null;
    }
  }, []);

  useEffect(() => {
      mediaImagesRef.current = mediaImages;
  }, [mediaImages]);

  useEffect(() => {
      let cancelled = false;

      const fetchImages = async () => {
          const neededNames = new Set();
          for (const device of devices) {
              const playing = devicePlaying[device.device_name];
              if (!playing) continue;
              if (playing.regularContentName) neededNames.add(playing.regularContentName);
              if (playing.speedingContentName) neededNames.add(playing.speedingContentName);
          }

          const namesToFetch = [...neededNames].filter((name) => !mediaImagesRef.current[name]);
          if (namesToFetch.length === 0) return;

          const fetched = {};
          await Promise.all(namesToFetch.map(async (name) => {
              const url = await fetchMediaImage(name);
              if (url) fetched[name] = url;
          }));

          if (cancelled || Object.keys(fetched).length === 0) return;

          setMediaImages((prev) => {
              const next = { ...prev, ...fetched };
              mediaImagesRef.current = next;
              return next;
          });
      };

      if (devices.length > 0 && Object.keys(devicePlaying).length > 0) {
          fetchImages();
      }

      return () => {
          cancelled = true;
      };
  }, [devices, devicePlaying, fetchMediaImage]);

  const previewFrameSx = {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const previewSlotStyle = {
    display: 'block',
    width: 64,
    height: 48,
    minWidth: 64,
    minHeight: 48,
    maxWidth: 64,
    maxHeight: 48,
    flex: '0 0 64px',
    overflow: 'hidden',
    lineHeight: 0,
  };

  const previewMediaSx = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  };

  const previewSpeedSx = (color) => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color,
    fontSize: '28px',
    lineHeight: 1,
    userSelect: 'none',
    m: 0,
    p: 0,
  });

  const renderPreviewFrame = (title, child) => (
    <Tooltip title={title}>
      <span style={previewSlotStyle}>
        <Box sx={previewFrameSx}>
          {child}
        </Box>
      </span>
    </Tooltip>
  );

  const renderSpeedDisplay = (speedLimit, isRegular) => (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        backgroundColor: 'black', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center'
      }}
    >
      <Typography 
        variant="h1" 
        sx={{ color: isRegular ? 'green' : 'red' }}
      >
        {speedLimit}
      </Typography>
    </Box>
  );

  const renderRegularImage = (playing) => {
    if (!playing) {
      return renderPreviewFrame('Regular', null);
    }

    if (playing.flash && !isFlashOn) {
      return renderPreviewFrame('Regular', null);
    }

    if (playing.regularDisplayMode === 2 || (playing.regularDisplayMode === 3 && !isSwitchOn)) {
      return renderPreviewFrame(
        'Regular',
        <Typography variant="h6" component="div" sx={previewSpeedSx('green')}>
          {playing.speedLimit}
        </Typography>
      );
    }

    const imageSrc = playing.regularContentName ? mediaImages[playing.regularContentName] : null;
    if (!imageSrc) {
      return renderPreviewFrame('Regular', null);
    }

    return renderPreviewFrame(
      'Regular',
      <Box
        component="img"
        src={imageSrc}
        alt={playing.regularContentName}
        sx={previewMediaSx}
      />
    );
  };

  const renderSpeedingImage = (playing) => {
    if (!playing) {
      return renderPreviewFrame('Speeding', null);
    }

    if (playing.flash && !isFlashOn) {
      return renderPreviewFrame('Speeding', null);
    }

    if (playing.speedingDisplayMode === 2 || (playing.speedingDisplayMode === 3 && !isSwitchOn)) {
      return renderPreviewFrame(
        'Speeding',
        <Typography variant="h6" component="div" sx={previewSpeedSx('red')}>
          {playing.speedLimit}
        </Typography>
      );
    }

    const imageSrc = playing.speedingContentName ? mediaImages[playing.speedingContentName] : null;
    if (!imageSrc) {
      return renderPreviewFrame('Speeding', null);
    }

    return renderPreviewFrame(
      'Speeding',
      <Box
        component="img"
        src={imageSrc}
        alt={playing.speedingContentName}
        sx={previewMediaSx}
      />
    );
  };

  const getColorDots = (deviceType) => {
    if (deviceType.includes('RGB')) {
      return rgbImage;
    }
    return rgImage;
  };
  

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          color: primaryColor 
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: `${textColor}` }}>
          Loading devices...
        </Typography>
      </Box>
    );
  }


  if (error) {
    return <div style={{ color: primaryColor }}>Error: {error}</div>;
  }

  return (
    <div className={`overview ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <Paper sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: secondaryColor,
        overflow: 'hidden',
      }}>
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}>
          <Table stickyHeader>
          <TableHead>
                <TableRow>
                  {['GROUP', 'LABEL', 'MODEL', 'LOCATION', 'CONTENTS', 'PENDING UPDATE', 'SIGNAL', 'BATTERY', 'TEMP °C', 'SPEED LIMIT', 'LAST UPDATE'].map((header) => (
                    <TableCell 
                      key={header} 
                      sx={{ 
                        backgroundColor: `${darkMode ? '#666666' : '#aaaaaa'} !important`,
                        color: `${textColor} !important`,
                        textAlign: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow sx={{ position: 'sticky', top: 56, zIndex: 1, backgroundColor: `${darkMode ? '#666666' : '#aaaaaa'} !important` }}>
                  {['GROUP', 'LABEL', 'MODEL', 'LOCATION'].map((header) => (
                    <TableCell key={header} sx={{ padding: '8px' }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        placeholder={`Search ${header.toLowerCase()}`}
                        onChange={(e) => handleSearchChange(e, header.toLowerCase())}
                        sx={{ width: '100%', backgroundColor: `${secondaryColor} !important`, color: `${textColor} !important` }}
                      />
                    </TableCell>
                  ))}
                  {['', '', '', '', '', '', ''].map((header) => (
                    <TableCell key={header} sx={{ padding: '8px' }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        placeholder={header}
                        disabled
                        sx={{ width: '100%', backgroundColor: `${secondaryColor} !important`, color: `${textColor} !important` }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            <TableBody>
              {filteredDevices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((device, index) => (
                  <TableRow 
                    key={device.id} 
                    hover 
                    onClick={() => handleDeviceClick(device.id)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: index % 2 ? secondaryColor : (darkMode ? '#262626' : '#f5f5f5'),
                      '&:hover': {
                        backgroundColor: darkMode ? '#333333' : '#e0e0e0',
                      },
                      height: 'auto',
                    }}
                  >
                    <TableCell sx={{ color: `${textColor} !important`, padding: '8px' }}>
                      {device.groups && device.groups.length > 0 ? (
                        <Box>
                          {device.groups.map((group, index) => (
                            <React.Fragment key={group.id}>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGroupClick(group.id);
                                }}
                                sx={{ color: textColor }}
                              >
                                {group.group_name}
                              </Link>
                              {index < device.groups.length - 1 ? ', ' : ''}
                            </React.Fragment>
                          ))}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>{device.device_name || 'Unknown'}</TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px', whiteSpace: 'nowrap' }}>
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <span>{device.device_type || 'Unknown'}</span>
                        <Box
                          component="img"
                          src={getColorDots(device.device_type)}
                          alt="Color Dots"
                          sx={{ width: 24, height: 24, mt: 1 }}
                        />
                      </Box>
                    </TableCell>                
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddrClick(device.id);
                          }}
                          sx={{ color: textColor }}
                        >
                          <Typography variant="body2" component="div">
                            {device.street_address}
                          </Typography>
                          <Typography variant="body2" component="div">
                            {device.suburb}
                          </Typography>
                          <Typography variant="body2" component="div">
                            {device.direction}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            height: 48,
                            minHeight: 48,
                            width: 136,
                            minWidth: 136,
                            maxWidth: 136,
                            gap: '8px',
                            margin: '0 auto',
                            overflow: 'hidden',
                          }}
                        >
                          {devicePlaying[device.device_name] !== undefined ? (
                            <>
                              {renderRegularImage(devicePlaying[device.device_name])}
                              {renderSpeedingImage(devicePlaying[device.device_name])}
                            </>
                          ) : (
                            <>
                              {renderPreviewFrame('Regular', <CircularProgress size={16} />)}
                              {renderPreviewFrame('Speeding', <CircularProgress size={16} />)}
                            </>
                          )}
                        </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ color: `${textColor}!important`, padding: '8px' }}>
                    <Box
                        sx={{
                          backgroundColor: device.update_todo_count === 0 ? 'green' : 'yellow',
                          color: 'black',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: device.update_todo_count === 0 ? 'darkgreen' : 'darkorange',
                            '&::after': {
                              content: `"${device.update_todo_count} pending update(s)"`,
                              position: 'absolute',
                              backgroundColor: 'rgba(0, 0, 0, 0.75)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            },
                          },
                          position: 'relative',
                        }}
                      >
                        {device.update_todo_count === 0 ? 'Up To Date' : 'Pending'}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>{getSignalIcon(device.dbm)}</TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>{getBatteryIcon(device.battery_rsoc)} {device.battery_rsoc >= 0 ? device.battery_rsoc : 'N/A'}%</TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        {device.battery_temperature !== null && device.battery_temperature !== undefined && device.battery_temperature >= 0
                          ? `${device.battery_temperature}°C`
                          : 'N/A°C'
                        }
                      </TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                      {devicePlaying[device.device_name] !== undefined 
                        ? devicePlaying[device.device_name].speedLimit !== undefined
                            ? `${devicePlaying[device.device_name].speedLimit} KM/H` 
                            : 'N/A'
                        : <CircularProgress size={20} />}
                    </TableCell>
                    <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                      {device.updated_at
                        ? (() => {
                            try {
                              const utcDate = new Date(device.updated_at);
                              const brisbaneTime = new Date(utcDate.getTime());
                              const formattedDate = brisbaneTime.toLocaleDateString('en-AU', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              });
                              const formattedTime = brisbaneTime.toLocaleTimeString('en-AU', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true
                              });

                              return (
                                <>
                                  <Typography variant="body2" component="div">
                                    {formattedDate}
                                  </Typography>
                                  <Typography variant="body2" component="div">
                                    {formattedTime}
                                  </Typography>
                                </>
                              );
                            } catch (error) {
                              console.error('Error parsing date:', error);
                              return 'Invalid Date';
                            }
                          })()
                        : 'Unknown'}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          rowsPerPageOptions={[100, 200, 500]}
          component="div"
          count={filteredDevices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: textColor,
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select, .MuiTablePagination-selectIcon': {
              color: `${textColor} !important`,
            },
            '.MuiTablePagination-actions': {
              color: textColor,
              '& .MuiIconButton-root.Mui-disabled': {
                color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        />
      </Paper>
    </div>
  );
  /*
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', backgroundColor: secondaryColor}}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ color: primaryColor }}>Group</TableCell>
              <TableCell style={{ color: primaryColor }}>Label</TableCell>
              <TableCell style={{ color: primaryColor }}>Type</TableCell>
              <TableCell style={{ color: primaryColor }}>Location</TableCell>
              <TableCell style={{ color: primaryColor }}>Contents</TableCell>
              <TableCell style={{ color: primaryColor }}>Template</TableCell>
              <TableCell style={{ color: primaryColor }}>Signal</TableCell>
              <TableCell style={{ color: primaryColor }}>Battery</TableCell>
              <TableCell style={{ color: primaryColor }}>Cabin °C</TableCell>
              <TableCell style={{ color: primaryColor }}>Records</TableCell>
              <TableCell style={{ color: primaryColor }}>Avg Speed</TableCell>
              <TableCell style={{ color: primaryColor }}>Speed Limit</TableCell>
              <TableCell style={{ color: primaryColor }}>Last Update</TableCell>
              <TableCell style={{ color: primaryColor }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((device) => (
                <React.Fragment key={device.id}>
                  <TableRow hover role="checkbox" tabIndex={-1}>
                    <TableCell>{device.group}</TableCell>
                    <TableCell>{device.device_name}</TableCell>
                    <TableCell>{device.device_type}</TableCell>
                    <TableCell>{`${device.street_address} ${device.direction}`}</TableCell>
                    <TableCell>{device.contents}</TableCell>
                    <TableCell>{device.template}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getSignalIcon(device.signal)}
                        {device.signal} dBm
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getBatteryIcon(device.battery)}
                        {device.battery}%
                      </Stack>
                    </TableCell>
                    <TableCell>{device.cabinTemp}°C</TableCell>
                    <TableCell>{device.records}</TableCell>
                    <TableCell>{device.avgSpeed} km/h</TableCell>
                    <TableCell>{device.speedLimit} km/h</TableCell>
                    <TableCell>{new Date(device.lastUpdate).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => handleExpandClick(device.id)}
                      >
                        {expandedDevice === device.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
                      <Collapse in={expandedDevice === device.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Details
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Group: {device.group}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Label: {device.label}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Type: {device.type}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Location: {device.location}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Direction: {device.direction}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Contents: {device.contents}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Template: {device.template}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Signal: {device.signal} dBm
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Battery: {device.battery}%
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Cabin °C: {device.cabinTemp}°C
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Records: {device.records}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Avg Speed: {device.avgSpeed} km/h
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Speed Limit: {device.speedLimit} km/h
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Last Update: {new Date(device.lastUpdate).toLocaleString()}
                          </Typography>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={devices.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
  */
};

export default Overview;
